/**
 * Provider Detail service — MOCK-FIRST (CT-3896).
 *
 * Synthesizes a rich provider-detail object from the same slugs used across the
 * catalog. Connected providers (a known set) carry instances/usage/activity;
 * everything else resolves as `not_connected` (in catalog, never connected).
 * Platform envelope, snake_case, `TODO(CT-3904 / backend)` per function.
 */

import { envelope } from '../shared/utils/mockServiceHelpers.js';
import { buildProviderUsage } from './providerUsageCatalog.js';
import { buildProviderCapabilities } from './providerCapabilityCatalog.js';
import {
  CONNECTED_PROVIDERS,
  isProviderDisconnected,
  markProviderDisconnected,
  __resetMockProvidersForTests,
} from './connectedProvidersCatalog.js';

export { CONNECTED_PROVIDERS, isProviderDisconnected, __resetMockProvidersForTests };

const PROVIDER_NAMES = {
  hubspot: 'HubSpot',
  stripe: 'Stripe',
  airtable: 'Airtable',
  figma: 'Figma',
  twilio: 'Twilio',
};

const titleCase = (slug) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Aggregate provider status from its instance statuses (mirrors CT-4197). */
const SEVERITY = ['auth_failed', 'auth_expired', 'provider_down', 'rate_limited', 'schema_drift', 'needs_mapping', 'auth_expiring'];
const aggregate = (statuses) => {
  const worst = SEVERITY.find((s) => statuses.includes(s));
  if (worst) return worst;
  if (statuses.some((s) => s === 'working')) return 'working';
  return statuses.length ? 'disconnected' : 'not_connected';
};

/**
 * Full provider detail for the Provider Detail page.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug`.
 */
export const getProvider = (slug) => {
  const base = CONNECTED_PROVIDERS[slug];
  const isConnected = Boolean(base) && !isProviderDisconnected(slug);
  const name = base?.name ?? PROVIDER_NAMES[slug] ?? titleCase(slug);
  const instanceStatuses = isConnected ? (base?.instances ?? []) : [];
  const status = isConnected ? aggregate(instanceStatuses) : 'not_connected';

  const instances = instanceStatuses.map((s, i) => ({
    id: `${slug}-inst-${i + 1}`,
    name: i === 0 ? `${name} — Production` : `${name} — Staging`,
    slug: i === 0 ? `${slug}-prod` : `${slug}-staging`,
    status: s,
    auth_method: base?.auth ?? 'oauth',
    managed: i === 0 ? 'aims' : 'byok',
    capabilities_active: i === 0 ? 5 : 3,
    last_run: '2026-08-02T18:20:00.000Z',
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
  }));

  const working = instanceStatuses.filter((s) => s === 'working').length;
  const needsAttention = instanceStatuses.filter(
    (s) => s !== 'working' && s !== 'disconnected' && s !== 'unknown',
  ).length;

  return Promise.resolve(
    envelope({
      slug,
      name,
      official: base?.official ?? true,
      category: base?.category ?? 'Custom / Build your own',
      vendor: base?.vendor ?? null,
      connection_state: isConnected ? 'connected' : 'not_connected',
      status,
      auth_method: base?.auth ?? null,
      last_run: isConnected ? '2026-08-02T18:20:00.000Z' : null,
      connected_at: isConnected ? '2026-06-14T10:00:00.000Z' : null,
      connected_by: isConnected ? { name: 'Danilo Petrov', initials: 'DP' } : null,
      instances,
      breakdown: { working_count: working, needs_attention_count: needsAttention },
      ...buildProviderCapabilities(slug, instances.length),
      studios: {
        governance: isConnected
          ? { enabled: true, usage: '12 policies', detail: 'updated 2h ago' }
          : { enabled: false },
        agentic: isConnected
          ? { enabled: true, usage: '4 workflows', detail: '2 agents' }
          : { enabled: false },
        workforce: { enabled: false },
      },
      about: `${name} connects your workspace so agents and workflows can act on ${name} data.`,
      use_cases: [
        `Let agents invoke ${name} actions on behalf of operators.`,
        `Index ${name} data as a Governance Knowledge Pack.`,
        `Trigger workflows when events happen in ${name}.`,
      ],
      scopes: ['read', 'write', 'admin'],
      details: {
        maintainer: base?.official ? 'AIMS OS' : 'Community',
        version: '1.4.0',
        changelog_url: '#changelog',
        docs_url: '#docs',
        support_email: 'support@aims-os.ai',
        data_residency: 'US-East · EU available',
        compliance: ['SOC 2', 'ISO 27001', 'GDPR'],
        security_review: '2026-03-10',
      },
    }),
  );
};

/**
 * Impact summary shown before disconnecting a provider (CT-4187).
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/disconnect-impact`.
 */
export const IMPACT_GROUPS = ['workflows', 'agents', 'networks', 'widgets'];

export const getDisconnectImpact = (slug) => {
  const base = CONNECTED_PROVIDERS[slug];
  const isConnected = Boolean(base) && !isProviderDisconnected(slug);
  if (!isConnected) {
    return Promise.resolve(
      envelope({ workflows: [], agents: [], networks: [], widgets: [], total: 0 }),
    );
  }
  const { consumers } = buildProviderUsage(slug);
  const groups = Object.fromEntries(IMPACT_GROUPS.map((key) => [key, []]));
  consumers.forEach((item) => {
    const key = `${item.type}s`;
    if (groups[key]) groups[key].push(item.name);
  });
  return Promise.resolve(envelope({ ...groups, total: consumers.length }));
};

/**
 * Disconnect a provider (CT-4187) — flips it to not_connected in the mock.
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/integrations/:slug/disconnect`.
 */
export const disconnectProvider = (slug) => {
  markProviderDisconnected(slug);
  return Promise.resolve(envelope({ slug, connection_state: 'not_connected' }));
};

/**
 * Provider configuration audit trail (CT-4184) — immutable, newest first.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/audit`.
 */
export const getProviderAudit = (slug) => {
  const isConnected = Boolean(CONNECTED_PROVIDERS[slug]);
  // Timestamps are relative to "now" so the table reads like the prototype
  // ("2 min ago", "1 hour ago") instead of stale absolute dates.
  const now = Date.now();
  const min = 60_000;
  const hr = 60 * min;
  const at = (ago) => new Date(now - ago).toISOString();
  const rows = isConnected
    ? [
        { id: 'evt_9f2a1c04', timestamp: at(2 * min), severity: 'info', event: 'integration.used_by_agent', actor: { name: 'Sales Triage Bot', kind: 'agent' }, resource: slug, studio: 'agentic', ip: '10.0.4.12', details: null },
        { id: 'evt_7c31b8e2', timestamp: at(5 * min), severity: 'info', event: 'integration.used_by_agent', actor: { name: 'PR Reviewer', kind: 'agent' }, resource: slug, studio: 'agentic', ip: '10.0.4.12', details: null },
        { id: 'evt_1a55d9f0', timestamp: at(14 * min), severity: 'warn', event: 'integration.health_degraded', actor: { name: 'System', kind: 'system' }, resource: slug, studio: null, ip: null, details: { reason: 'Webhook retries exceeded' } },
        { id: 'evt_4b0e2277', timestamp: at(18 * min), severity: 'info', event: 'integration.used_by_agent', actor: { name: 'Knowledge Indexer', kind: 'agent' }, resource: slug, studio: 'governance', ip: '10.0.4.12', details: null },
        { id: 'evt_a2f9c6d1', timestamp: at(hr), severity: 'warn', event: 'integration.health_degraded', actor: { name: 'System', kind: 'system' }, resource: slug, studio: null, ip: null, details: { reason: 'Webhook retries exceeded' } },
        { id: 'evt_88d1e430', timestamp: at(4 * hr), severity: 'info', event: 'integration.used_by_agent', actor: { name: 'Onboarding Bot', kind: 'agent' }, resource: slug, studio: 'agentic', ip: '10.0.4.12', details: null },
        { id: 'evt_5e77a0b9', timestamp: at(12 * hr), severity: 'info', event: 'integration.permissions_changed', actor: { name: 'María González', kind: 'user' }, resource: slug, studio: null, ip: '192.168.1.42', details: { added_scopes: ['records:write'] } },
        { id: 'evt_c9043f1a', timestamp: at(13 * hr), severity: 'warn', event: 'distribution.surface_hidden', actor: { name: 'María González', kind: 'user' }, resource: slug, studio: null, ip: '192.168.1.42', details: null },
        { id: 'evt_2fb6e881', timestamp: at(26 * hr), severity: 'crit', event: 'integration.health_failed', actor: { name: 'System', kind: 'system' }, resource: slug, studio: 'admin', ip: null, details: { reason: 'Auth token revoked by provider' } },
      ]
    : [];
  return Promise.resolve(envelope(rows, { total: rows.length }));
};

/**
 * Provider lifecycle activity events (CT-4183) — macro history, newest first.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/activity`.
 */
export const getProviderActivity = (slug) => {
  const base = CONNECTED_PROVIDERS[slug];
  const isConnected = Boolean(base);
  const name = base?.name ?? PROVIDER_NAMES[slug] ?? titleCase(slug);
  const instanceCount = base?.instances?.length ?? 0;
  const primaryName = `${name} — Production`;
  const secondaryName = `${name} — Staging`;

  const events = isConnected
    ? [
        ...(instanceCount > 0
          ? [
              {
                id: 'a1',
                timestamp: '2026-08-02T18:20:00.000Z',
                event_type: 'credentials_rotated',
                target: primaryName,
                target_instance_id: `${slug}-inst-1`,
                actor: 'Danilo Petrov',
              },
              {
                id: 'a2',
                timestamp: '2026-07-30T09:10:00.000Z',
                event_type: 'instance_created',
                target: instanceCount > 1 ? secondaryName : primaryName,
                target_instance_id: `${slug}-inst-${instanceCount > 1 ? 2 : 1}`,
                actor: 'Maria Gomez',
              },
            ]
          : []),
        {
          id: 'a3',
          timestamp: '2026-06-14T10:00:00.000Z',
          event_type: 'integration_added',
          target: name,
          actor: 'Danilo Petrov',
        },
      ]
    : [];

  return Promise.resolve(envelope(events, { total: events.length }));
};

/**
 * Aggregate usage stats + searchable consumers for the Usage tab (CT-4182).
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/:slug/usage`.
 */
export const getProviderUsage = (slug) => Promise.resolve(envelope(buildProviderUsage(slug)));
